import ApiService from './api'
import AppURLs from '../config/urls'

class SalesService {
  /**
   * Get all sales/completed orders with filtering
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise<Object>} - API response
   */
  async getAllSales(params = {}, config = {}) {
    try {
      console.log('💰 Fetching all sales with params:', params)
      
      // Add default filters for sales (completed, paid orders)
      const salesParams = {
        orderStatus: 'completed',
        paymentStatus: 'paid',
        ...params
      }
      
      const response = await ApiService.get(AppURLs.ORDERS.GET_ALL, salesParams, config)
      console.log('✅ Sales fetched successfully:', response)
      
      // If we have a search term and the backend didn't filter, apply client-side filtering
      if (params.search && params.search.trim() && response.data?.orders) {
        const searchTerm = params.search.trim().toLowerCase()
        console.log('🔍 Applying client-side search filter for:', searchTerm)
        
        const filteredOrders = response.data.orders.filter(order => {
          // Search in multiple fields
          const searchFields = [
            order.orderNumber || '',
            order.order_number || '',
            order.customerName || '',
            order.customer_name || '',
            order.customerMobile || '',
            order.customer_mobile || '',
            order.customer?.name || '',
            order.customer?.mobile || ''
          ].map(field => String(field).toLowerCase())
          
          return searchFields.some(field => field.includes(searchTerm))
        })
        
        console.log(`🔍 Client-side filter: ${response.data.orders.length} → ${filteredOrders.length} orders`)
        
        // Return the filtered response
        return {
          ...response,
          data: {
            ...response.data,
            orders: filteredOrders
          }
        }
      }
      
      return response
    } catch (error) {
      console.error('❌ Error fetching sales:', error)
      throw error
    }
  }

  /**
   * Get sales statistics
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise<Object>} - API response
   */
  async getSalesStats(params = {}) {
    try {
      console.log('📊 Fetching sales statistics with params:', params)
      
      const salesParams = {
        orderStatus: 'completed',
        ...params
      }
      
      const response = await ApiService.get(AppURLs.ORDERS.STATS, salesParams)
      
      console.log('✅ Sales statistics fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching sales statistics:', error)
      throw error
    }
  }

  /**
   * Get sale by ID (order details)
   * @param {string} id - Sale/Order ID
   * @returns {Promise<Object>} - API response
   */
  async getSaleById(id) {
    try {
      console.log('💰 Fetching sale by ID:', id)
      
      // Try the direct endpoint first
      try {
        const endpoint = ApiService.replaceUrlParams(AppURLs.ORDERS.GET_BY_ID, { id })
        console.log('📍 Trying endpoint:', endpoint)
        const response = await ApiService.get(endpoint)
        console.log('✅ Sale fetched successfully via GET_BY_ID:', response)
        return response
      } catch (error) {
        console.warn('⚠️ GET_BY_ID failed, trying alternative approach:', error.message)
        
        // Fallback: Use GET_ALL with ID filter
        const response = await ApiService.get(AppURLs.ORDERS.GET_ALL, { 
          orderStatus: 'completed',
          orderId: id 
        })
        
        console.log('✅ Sale fetched successfully via GET_ALL filter:', response)
        return response
      }
      
    } catch (error) {
      console.error('❌ Error fetching sale:', error)
      throw error
    }
  }

  /**
   * Download sale receipt/invoice
   * @param {string} id - Sale/Order ID
   * @param {string} format - Download format (pdf, excel)
   * @returns {Promise<Blob>} - File blob
   */
  async downloadSaleReceipt(id, format = 'pdf') {
    try {
      console.log('📥 Downloading sale receipt:', { id, format })
      
      const endpoint = ApiService.replaceUrlParams(AppURLs.ORDERS.RECEIPT, { id })
      const params = { format }
      
      const response = await ApiService.get(endpoint, params, { responseType: 'blob' })
      
      console.log('✅ Sale receipt downloaded successfully')
      return response
    } catch (error) {
      console.error('❌ Error downloading sale receipt:', error)
      
      // If receipt endpoint doesn't exist, try alternative approaches
      if (error.response?.status === 404) {
        console.log('📄 Receipt endpoint not available, trying alternative download...')
        // You could implement alternative download logic here if needed
        throw new Error('Receipt download is not available for this order')
      }
      
      throw error
    }
  }

  /**
   * Export sales data
   * @param {Object} params - Query parameters for filtering
   * @param {string} format - Export format (csv, excel, pdf)
   * @returns {Promise<Blob>} - File blob
   */
  async exportSalesData(params = {}, format = 'excel') {
    try {
      console.log('📤 Exporting sales data:', { params, format })
      
      const salesParams = {
        orderStatus: 'completed',
        export: true,
        format,
        ...params
      }
      
      // Use dedicated export endpoint if available, otherwise use GET_ALL
      const endpoint = AppURLs.ORDERS.EXPORT || AppURLs.ORDERS.GET_ALL
      const response = await ApiService.get(endpoint, salesParams, { responseType: 'blob' })
      
      console.log('✅ Sales data exported successfully')
      return response
    } catch (error) {
      console.error('❌ Error exporting sales data:', error)
      throw error
    }
  }

  /**
   * Search sales
   * @param {string} searchTerm - Search term
   * @param {Object} filters - Additional filters
   * @returns {Promise<Object>} - API response
   */
  async searchSales(searchTerm, filters = {}) {
    try {
      console.log('🔍 Searching sales:', { searchTerm, filters })
      
      const params = {
        orderStatus: 'completed',
        search: searchTerm,
        ...filters
      }
      
      const response = await ApiService.get(AppURLs.ORDERS.SEARCH, params)
      
      console.log('✅ Sales search completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error searching sales:', error)
      throw error
    }
  }

  /**
   * Get sales by branch
   * @param {string} branchId - Branch ID
   * @param {Object} params - Additional query parameters
   * @returns {Promise<Object>} - API response
   */
  async getSalesByBranch(branchId, params = {}) {
    try {
      console.log('🏢 Fetching sales by branch:', branchId)
      
      const salesParams = {
        orderStatus: 'completed',
        branch: branchId,
        ...params
      }
      
      const response = await ApiService.get(AppURLs.ORDERS.GET_ALL, salesParams)
      
      console.log('✅ Branch sales fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching branch sales:', error)
      throw error
    }
  }

  /**
   * Get sales by customer
   * @param {string} customerId - Customer ID
   * @param {Object} params - Additional query parameters
   * @returns {Promise<Object>} - API response
   */
  async getSalesByCustomer(customerId, params = {}) {
    try {
      console.log('👥 Fetching sales by customer:', customerId)
      
      const salesParams = {
        orderStatus: 'completed',
        customer: customerId,
        ...params
      }
      
      const response = await ApiService.get(AppURLs.ORDERS.GET_ALL, salesParams)
      
      console.log('✅ Customer sales fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching customer sales:', error)
      throw error
    }
  }

  /**
   * Get sales by date range
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @param {Object} params - Additional query parameters
   * @returns {Promise<Object>} - API response
   */
  async getSalesByDateRange(startDate, endDate, params = {}) {
    try {
      console.log('📅 Fetching sales by date range:', { startDate, endDate })
      
      const salesParams = {
        orderStatus: 'completed',
        startDate,
        endDate,
        ...params
      }
      
      const response = await ApiService.get(AppURLs.ORDERS.GET_ALL, salesParams)
      
      console.log('✅ Date range sales fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching date range sales:', error)
      throw error
    }
  }

  /**
   * Process response data
   * @param {Object} response - API response
   * @returns {Object} - Processed data
   */
  processResponse(response) {
    if (!response || typeof response !== 'object') {
      console.warn('⚠️ Invalid sales response received')
      return { sales: [], pagination: {}, stats: {} }
    }

    // Handle the actual API response structure
    if (response.success && response.data && response.data.orders) {
      return {
        sales: Array.isArray(response.data.orders) ? response.data.orders : [],
        pagination: {
          page: response.data.page || 1,
          limit: response.data.limit || 10,
          total: response.data.totalResults || 0,
          pages: response.data.totalPages || 1
        },
        stats: response.data.stats || {}
      }
    }

    // Handle different response structures (fallback)
    if (response.data) {
      return {
        sales: Array.isArray(response.data) ? response.data : response.data.orders || [],
        pagination: response.pagination || {},
        stats: response.stats || {}
      }
    }

    if (response.orders) {
      return {
        sales: Array.isArray(response.orders) ? response.orders : [],
        pagination: response.pagination || {},
        stats: response.stats || {}
      }
    }

    if (Array.isArray(response)) {
      return {
        sales: response,
        pagination: {},
        stats: {}
      }
    }

    console.warn('⚠️ Unknown sales response structure:', response)
    return { sales: [], pagination: {}, stats: {} }
  }

  /**
   * Parse API response and handle different formats
   * @param {Object} response - Raw API response
   * @returns {Object} - Standardized response
   */
  parseResponse(response) {
    try {
      const processedData = this.processResponse(response)
      
      console.log('📊 Processed sales response:', {
        salesCount: processedData.sales.length,
        hasPagination: !!Object.keys(processedData.pagination).length,
        hasStats: !!Object.keys(processedData.stats).length
      })

      return processedData
    } catch (error) {
      console.error('❌ Error parsing sales response:', error)
      return { sales: [], pagination: {}, stats: {} }
    }
  }
}

export default new SalesService()